// srt-results-charts.js
import Chart from 'chart.js/auto';
import annotationPlugin from 'chartjs-plugin-annotation';
Chart.register(annotationPlugin);

import { setupBootstrapTabs } from '../utils.js';
import { mapTyreConfig } from '../../common/srt/srt-common-ui-map.js';
import { getGML } from '../../common/srt/srt-common-compliance.js';
import { min } from 'lodash';
const currentPointColor = '#1561a2'; // current point

// Setup the dynamic tab listener for the results tab - required since bootstrap doesn't bind correctly
export function setupGraphsTabListener(unitIndex, onShowCallbackGraphs) {
   // Attach a shown handler to the results sub tabs
   setupBootstrapTabs(`#results-tabs-${unitIndex}`, (panelId, element) => {
      //console.log('Tab shown:', panelId);
      if (panelId === `#graphs-${unitIndex}`) {
         // Call the callback function when the graphs tab is shown
         onShowCallbackGraphs(unitIndex);
      }
   });
}

export function refreshCogSRTChart(unitIdx, inputData, currentSRT, targetSRT) {
   // Build a chart that varies CoG height and shows SRT
   let xAxisParam = 'cogHeight';
   // The starting value for gross mass is tare masses
   let min = 1.4;
   let max = inputData['load-height-max'] || 3.0; // Default max value based on input
   let step = 0.1;

   // cog charts can't have deck style as non standard.
   const dataWithStandardDeck = { ...inputData, 'deck-style': 'standard' };
   const params = {
      unitIndex: unitIdx,
      inputs: dataWithStandardDeck,
      xAxisParam,
      min,
      max,
      step,
   };
   refreshSRTChart(params, 'srt-cog-chart', currentSRT, targetSRT);
}

export function refreshPayloadSRTChart(unitIdx, inputData, currentSRT, targetSRT) {
   // Build a chart that varies payload according to front / rear ratio and shows SRT
   let xAxisParam = 'payload';

   let min = 0; // start at 0kg payload
   let max = maxChartPayload(inputData) + 500;
   let step = 1000; // step by 1000kg increments

   const params = {
      unitIndex: unitIdx,
      inputs: inputData,
      xAxisParam,
      min,
      max,
      step,
   };
   //console.log('Refreshing payload SRT chart with params:', params);
   refreshSRTChart(params, 'srt-payload-chart', currentSRT, targetSRT);
}

function refreshSRTChart(params, canvasId, currentSRT, targetSRT) {
   $.ajax({
      url: '/api/srt-sensitivity',
      method: 'POST',
      contentType: 'application/json',
      data: JSON.stringify(params),
      success: function (data) {
         const { labels, srtValues, xAxisLabel, dataFieldError, reason } = data.curveData;
         if (dataFieldError) {
            console.log('SRT curve data validation error, aborting chart render');
            $(`#${canvasId}`).hide();
            return;
         }
         if (!labels || !srtValues || labels.length === 0 || srtValues.length === 0) {
            console.log('SRT curve data is empty or invalid, aborting chart render');
            $(`#${canvasId}`).hide();
            return;
         }
         let trimCurveData = trimChartData(data.curveData);
         renderSRTChart(canvasId, trimCurveData, {
            xLabel: data.curveData.xAxisLabel,
            targetSRT: targetSRT,
            currentSRT: currentSRT,
         });
      },
      error: function (xhr, status, error) {
         console.error('SRT curve failed getting a result:', error);
      },
   });
}

function trimChartData(curveData) {
   // Trim the chart data that is outside of SRT range, < 0.1 and > 0.9
   const { labels, srtValues } = curveData;
   const trimmedLabels = [];
   const trimmedSRTValues = [];
   for (let i = 0; i < labels.length; i++) {
      if (srtValues[i] >= 0.05 && srtValues[i] <= 0.9) {
         trimmedLabels.push(labels[i]);
         trimmedSRTValues.push(srtValues[i]);
      }
   }
   return {
      labels: trimmedLabels,
      srtValues: trimmedSRTValues,
   };
}

/** * Generate the maximum payload for the chart based on GML limits
 * @param {Object} inputData - the SRT input data object
 * @returns {number} - the maximum payload mass in kg
 */
function maxChartPayload(inputData) {
   const { vehicleType, frontAxles, rearAxles } = inputData;
   const frontConfig = mapTyreConfig(inputData['f-tyre-config']);
   const rearConfig = mapTyreConfig(inputData['r-tyre-config']);
   // Get the maximum gross mass limits for this vehicle type and axle configuration
   const gml = getGML({
      vehicleType,
      frontAxles,
      rearAxles,
      frontConfig,
      rearConfig,
   });

   // tare masses for front and rear axles
   const fTare = inputData['f-tare-mass'] || 0;
   const rTare = inputData['r-tare-mass'] || 0;

   // total max allowable payload for this vehicle/axles
   const gmlPayload = gml.front.standard + gml.rear.standard - fTare - rTare;

   // ensure that this payload is more than the input payload
   const hasFrontAxles = inputData['frontAxles'] > 0;
   const currentPayload = hasFrontAxles
      ? inputData['f-payload-mass'] + inputData['r-payload-mass']
      : inputData['r-payload-mass'];
   const maxPayload = Math.max(gmlPayload, currentPayload);

   return maxPayload;
}

/**
 * Render a Chart.js SRT sensitivity chart (e.g. SRT vs CoG Height)
 * @param {string} canvasId - the ID of the <canvas> element (e.g. 'srt-cog-chart')
 * @param {Object} curveData - output from generateSRTCurve()
 * @param {Object} opts - config options: xLabel, targetSRT
 */
function renderSRTChart(canvasId, curveData, opts = {}) {
   const ctx = document.getElementById(canvasId).getContext('2d');
   const { labels, srtValues } = curveData;
   const { xLabel = 'Input', targetSRT = 0.35, currentSRT = 0.35 } = opts;
   //console.log('Rendering SRT chart with data:', opts);
   let closestIndex = 0;
   let smallestDelta = Infinity;

   // reverse-map the current SRT back to the nearest point on the curve
   srtValues.forEach((srt, i) => {
      const delta = Math.abs(srt - currentSRT);
      if (delta < smallestDelta) {
         smallestDelta = delta;
         closestIndex = i;
      }
   });

   const displayPoint = {
      x: labels[closestIndex],
      y: srtValues[closestIndex],
   };

   // Clean up existing chart if already rendered
   if (ctx._chartInstance) {
      ctx._chartInstance.destroy();
   }

   const dataset = {
      label: 'SRT (g)',
      data: labels.map((x, i) => ({ x, y: srtValues[i] })),
      borderColor: '#595959', // dark grey line
      backgroundColor: '#595959',
      fill: false,
      pointRadius: 2,
      tension: 0.2,
   };

   const maxX =
      canvasId === 'srt-payload-chart'
         ? Math.max(...dataset.data.map((p) => p.x)) + 500
         : Math.max(...dataset.data.map((p) => p.x)) + 0.4;

   const chart = new Chart(ctx, {
      type: 'line',
      data: {
         datasets: [dataset],
      },
      options: {
         responsive: true,
         scales: {
            x: {
               title: {
                  display: true,
                  text: xLabel,
               },
               type: 'linear',
               beginAtZero: false,
               max: canvasId === 'srt-payload-chart' ? maxX : null,
            },
            // SRT
            y: {
               title: {
                  display: true,
                  text: 'SRT (g)',
               },
               min: 0,
               max: 0.9,
            },
         },
         plugins: {
            legend: { display: false },
            tooltip: {
               callbacks: {
                  label: (ctx) => `SRT: ${ctx.parsed.y.toFixed(2)}g at ${ctx.parsed.x}`,
               },
            },
            annotation: {
               annotations: {
                  dangerZone: {
                     type: 'box',
                     yMin: 0,
                     yMax: targetSRT,
                     backgroundColor: 'rgba(255, 0, 0, 0.1)',
                     borderWidth: 0,
                  },
                  safeZone: {
                     type: 'box',
                     yMin: targetSRT,
                     yMax: 10, // or your max chart y
                     backgroundColor: 'rgba(0, 200, 0, 0.1)',
                     borderWidth: 0,
                  },
                  currentDisplayPoint: {
                     type: 'point',
                     xValue: displayPoint.x,
                     yValue: displayPoint.y,
                     backgroundColor: currentPointColor,
                     radius: 5,
                     label: {
                        content: `Current (${displayPoint.y.toFixed(2)}g)`,
                        enabled: true,
                        position: 'top',
                     },
                  },
               },
            },
         },
      },
   });

   // Track for cleanup
   ctx._chartInstance = chart;
}

//
// Feasible boundary chart - finds payload/cog or payload/load height values that meet/exceed target SRT
//
export function refreshFeasibleBoundaryChart(unitIdx, inputData, targetSRT, currentPayloadCog) {
   const mode = $('input[name="srt-boundary-mode"]:checked').val(); // 'cog' or 'load'

   const hasFrontAxles = inputData['frontAxles'] > 0;
   const minPayload = 0;
   const currentPayload = hasFrontAxles
      ? inputData['f-payload-mass'] + inputData['r-payload-mass']
      : inputData['r-payload-mass'];
   const maxPayload = maxChartPayload(inputData) + 500; // add a bit of headroom
   const payloadStep = 1000;

   const params = {
      unitIndex: unitIdx,
      //: inputData,
      minPayload,
      maxPayload,
      payloadStep,
      targetSRT,
      mode,
   };
   // extract current vehicle payload heights
   const loadBedHeightMax = getCurrentLoadBedMax(inputData); // max load bed height based on deck style or fallback

   const loadHeightMaxUser = getCurrentLoadHeightMax(inputData); // maximum load height based on deck style
   const loadHeightMax = loadHeightMaxUser + 2.0; // show the user what happens if they could load higher by 2m
   const cogMax = loadHeightMax / 2; // just assume its half the load height as max for now based on deck style

   const currentLoadHeight = getCurrentLoadHeight(inputData); // current load height based on deck style

   const minLoadOffset = 0.2; // minimum height above load bed assumption for the charts

   if (mode === 'cog') {
      // Graph the coG from load bed height + min load height to max load height
      params.minCgHeight = loadBedHeightMax + minLoadOffset || 1.4;
      params.maxCgHeight = cogMax || 3.0;
      params.cgStep = 0.1;
   } else if (mode === 'load') {
      // graph the load height from just above the bed to max allowable load height
      params.minLoadHeight = loadBedHeightMax + minLoadOffset || 1.4;
      params.maxLoadHeight = loadHeightMax;
      params.loadStep = 0.1;
      params.minY = params.minLoadHeight;
   }
   // cog charts can't have deck style as non standard, we will vary the cog
   params.inputs = { ...inputData, 'deck-style': 'standard' };

   refreshBoundaryChart(
      params,
      'srt-feasible-boundary-chart',
      targetSRT,
      currentPayload,
      currentPayloadCog,
      currentLoadHeight
   );
}

function getCurrentLoadBedMax(inputData) {
   // load bed height based on the deck style.  The load height graphs needs to assume a standard body style.
   // In the case of the other styles, simply take the minimum (or front and rear) of the load bed height.

   const defaultHeight = 1.2; // default load bed height if not specified
   switch (inputData['deck-style']) {
      case 'standard':
         return Number(inputData['load-bed-height']) || defaultHeight;
      case 'sloping':
         return Math.max(
            Number(inputData['load-bed-height-front']) || defaultHeight,
            Number(inputData['load-bed-height-rear']) || defaultHeight
         );
      case 'step':
         return Math.max(
            Number(inputData['deck1-height']) || defaultHeight,
            Number(inputData['deck2-height']) || defaultHeight
         );
      default:
         return defaultHeight;
   }
}

function getCurrentLoadHeightMax(inputData) {
   // maximum load height based on the deck style.  The load height graphs needs to assume a standard body style.
   // In the case of the other styles, simply take the maximum (or front and rear) of the load height.
   const defaultHeight = 4.6; // default load height if not specified

   switch (inputData['deck-style']) {
      case 'standard':
         return Number(inputData['load-height-max']) || defaultHeight;
      case 'sloping':
         return Math.max(
            Number(inputData['load-height-front']) || defaultHeight,
            Number(inputData['load-height-rear']) || defaultHeight
         );
      case 'step':
         return Math.max(Number(inputData['deck-load-height-max']) || defaultHeight);
      default:
         return defaultHeight;
   }
}

function getCurrentLoadHeight(inputData) {
   // load height based on the deck style.  The load height graphs needs to assume a standard body style.
   // In the case of the other styles, simply take the maximum (or front and rear) of the load height.
   const defaultHeight = 3.8; // default load bed height if not specified

   switch (inputData['deck-style']) {
      case 'standard':
         return Number(inputData['load-height-max']) || defaultHeight;
      case 'sloping':
         const fLoadHeight = Number(inputData['load-height-front']);
         const rLoadHeight = Number(inputData['load-height-rear']);
         if (!isFinite(fLoadHeight) || !isFinite(rLoadHeight)) {
            return defaultHeight;
         }
         // average the front and rear load heights
         return Math.round((fLoadHeight + rLoadHeight) * 50) / 100;
      case 'step':
         const stepLoadHeight = Number(inputData['deck-load-height-max']);
         return stepLoadHeight || defaultHeight;
      default:
         return defaultHeight;
   }
}

function refreshBoundaryChart(params, canvasId, targetSRT, currentPayload, currentCog, currentLoadHeight) {
   $.ajax({
      url: '/api/srt-feasible-boundary',
      method: 'POST',
      contentType: 'application/json',
      data: JSON.stringify(params),
      success: function (data) {
         const { boundaryData, mode } = data;
         if (!boundaryData || !boundaryData.boundaryPoints || boundaryData.boundaryPoints.length === 0) {
            console.log('Feasible boundary data is empty or invalid, aborting chart render');
            $(`#${canvasId}`).hide();
            return;
         }

         renderBoundaryChart(canvasId, boundaryData, {
            targetSRT,
            currentPayload,
            currentCog,
            currentLoadHeight,
            mode,
            minY: params.minY || 0,
         });
      },
      error: function (xhr, status, error) {
         console.error('Feasible boundary chart failed to get a result:', error);
      },
   });
}

function renderBoundaryChart(canvasId, boundaryData, opts = {}) {
   const ctx = document.getElementById(canvasId).getContext('2d');
   const {
      targetSRT = 0.35,
      currentPayload = null,
      currentCog = null,
      currentLoadHeight = null,
      mode = 'cog',
      minY = 0,
   } = opts;

   // update of title and description text above chart
   const vehicleMessage = ` <em>Current Vehicle:</em> ${currentPayload} kg of payload with an effective load height of ${currentLoadHeight} m and center of gravity height of ${currentCog}.`;
   if (mode === 'cog') {
      $('#boundary-chart-title').text('CoG Height vs Payload to meet SRT target');
      $('#boundary-chart-description').text(
         'This graph shows the maximum centre of gravity height allowed for a given loaded payload that meets the target SRT.  The green area indicates compliant combinations of payload and CoG height.'
      );
      $('#boundary-chart-vehicle').html(vehicleMessage);
   } else {
      $('#boundary-chart-title').text('Load Height vs Payload to meet SRT target');
      $('#boundary-chart-description').text(
         'This graph shows the maximum load height from ground allowed for a given payload mass to achieve the target SRT. The green area indicates compliant combinations of payload and load height.'
      );
      $('#boundary-chart-vehicle').html(vehicleMessage);
   }

   const { boundaryPoints } = boundaryData;
   const sortedPoints = [...boundaryPoints].sort((a, b) => a.x - b.x);

   const boundaryDataset = {
      label: `SRT = ${targetSRT}g`,
      data: sortedPoints.map((pt) => ({ x: pt.x, y: pt.y, srt: pt.srt })),
      borderColor: '#595959',
      backgroundColor: '#595959',
      fill: false,
      pointRadius: 2,
      tension: 0.1,
   };

   const greenFillData = [
      { x: sortedPoints[0].x, y: 0 },
      ...sortedPoints.map((pt) => ({ x: pt.x, y: pt.y })),
      { x: sortedPoints[sortedPoints.length - 1].x, y: 0 },
   ];

   const greenRegionDataset = {
      label: 'Compliant',
      data: greenFillData,
      backgroundColor: 'rgba(0, 200, 0, 0.15)',
      fill: 'origin',
      pointRadius: 0,
      borderWidth: 0,
      type: 'line',
      tension: 0,
   };

   const maxX = Math.max(...sortedPoints.map((p) => p.x)) + 500;
   const maxY = Math.max(...sortedPoints.map((p) => p.y)) + 0.5;

   const yLabel = mode === 'load' ? 'Load Height (m)' : 'CoG Height (m)';
   const annotationY = mode === 'load' ? currentLoadHeight : currentCog; // switch the annotation (current vehicle as a point on chart) y value based on mode

   const annotations = {};
   if (currentPayload !== null && currentCog !== null) {
      annotations.currentPoint = {
         type: 'point',
         xValue: currentPayload,
         yValue: annotationY,
         backgroundColor: currentPointColor,
         radius: 5,
         label: {
            content: 'Current',
            enabled: true,
            position: 'top',
         },
      };
      annotations.currentPayloadLine = {
         type: 'line',
         xMin: currentPayload,
         xMax: currentPayload,
         yMin: 0,
         yMax: annotationY,
         borderColor: currentPointColor,
         borderWidth: 1,
         borderDash: [5, 5],
      };
      annotations.currentCogLine = {
         type: 'line',
         yMin: annotationY,
         yMax: annotationY,
         xMin: 0,
         xMax: currentPayload,
         borderColor: currentPointColor,
         borderWidth: 1,
         borderDash: [5, 5],
      };
   }

   if (ctx._chartInstance) {
      ctx._chartInstance.destroy();
   }

   const chart = new Chart(ctx, {
      type: 'line',
      data: {
         datasets: [greenRegionDataset, boundaryDataset],
      },
      options: {
         responsive: true,
         scales: {
            x: {
               title: {
                  display: true,
                  text: 'Payload Mass (kg)',
               },
               type: 'linear',
               beginAtZero: true,
               max: maxX,
            },
            y: {
               title: {
                  display: true,
                  text: yLabel,
               },
               min: minY,
               suggestedMax: maxY,
            },
         },
         plugins: {
            legend: { display: true },
            tooltip: {
               callbacks: {
                  label: (ctx) => {
                     const { x, y } = ctx.parsed;
                     const { srt } = ctx.raw;
                     return mode === 'load'
                        ? `Payload: ${x.toFixed(0)} kg, Load Height: ${y.toFixed(2)} m, SRT: ${
                             srt?.toFixed(3) ?? 'N/A'
                          }g`
                        : `Payload: ${x.toFixed(0)} kg, CoG: ${y.toFixed(2)} m, SRT: ${srt?.toFixed(3) ?? 'N/A'}g`;
                  },
               },
            },

            annotation: { annotations },
         },
      },
   });

   ctx._chartInstance = chart;
}
